import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import StepReviewQuestions from './campaign-steps/StepReviewQuestions';
import StepEmailContent from './campaign-steps/StepEmailContent';
import StepThankYouPages from './campaign-steps/StepThankYouPages';
import dynamicOptionsService from '../../../utils/dynamicOptionsService';

const CampaignBuilder = ({ campaignData, organizationData, onDataChange, onMessage, onSave, onSaveStep }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [organizationServices, setOrganizationServices] = useState([]);
  const [step3ActiveTab, setStep3ActiveTab] = useState('high');

  // Fetch organization services for preview
  useEffect(() => {
    if (organizationData?.id) {
      const fetchServices = async () => {
        try {
          const services = await dynamicOptionsService.getPrimaryServices(organizationData.id);
          setOrganizationServices(services || []);
        } catch (error) {
          console.error('Error fetching organization services:', error);
          setOrganizationServices([]);
        }
      };
      fetchServices();
    }
  }, [organizationData?.id]);

  // Memoize steps array to prevent unnecessary re-renders
  const steps = useMemo(() => [
    {
      id: 1,
      title: 'Review Selection',
      description: 'Request flow settings',
      icon: 'Star'
    },
    {
      id: 2,
      title: 'Customer Messaging',
      description: 'Text & Email Settings',
      icon: 'Mail'
    },
    {
      id: 3,
      title: 'Feedback',
      description: 'Prompt & Thank You',
      icon: 'Heart'
    }
  ], []);

  const handleStepChange = useCallback((stepNumber) => {
    setCurrentStep(stepNumber);
  }, []);

  // Memoize sample service to prevent recalculation
  const sampleService = useMemo(() => {
    return organizationServices.length > 0 
      ? organizationServices[0].name 
      : 'HVAC Service';
  }, [organizationServices]);

  // Helper function to replace template variables with sample data for preview - memoized for performance
  const replaceVariablesForPreview = useCallback((text) => {
    if (!text) return text;
      
    return text
      .replace(/\{\{business\.name\}\}/g, organizationData?.name || 'Your Business')
      .replace(/\{\{contact\.name\}\}/g, 'John Smith')
      .replace(/\{\{contact\.email\}\}/g, 'john.smith@email.com')
      .replace(/\{\{service\.name\}\}/g, sampleService);
  }, [organizationData?.name, sampleService]);

  const renderTopTabs = () => (
    <div className="border-b border-gray-200 bg-white">
      <div className="flex flex-col lg:flex-row lg:items-center px-4 lg:px-6">
        {/* Tabs Container */}
        <div className="flex items-center space-x-4 lg:space-x-8 overflow-x-auto py-2 lg:py-0">
          {steps.map((step, index) => (
            <button
              key={step.id}
              onClick={() => handleStepChange(step.id)}
              className={`flex items-center space-x-2 lg:space-x-3 py-2 lg:py-4 border-b-2 transition-colors whitespace-nowrap ${
                currentStep === step.id
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <div className={`flex items-center justify-center w-6 h-6 lg:w-8 lg:h-8 rounded-full text-xs lg:text-sm font-semibold ${
                currentStep === step.id
                  ? 'bg-primary text-white'
                  : currentStep > step.id
                  ? 'bg-green-100 text-green-600'
                  : 'bg-gray-100 text-gray-400'
              }`}>
                {currentStep > step.id ? (
                  <Icon name="Check" size={12} className="lg:hidden" />
                ) : currentStep > step.id ? (
                  <Icon name="Check" size={16} className="hidden lg:block" />
                ) : (
                  `0${step.id}`
                )}
              </div>
              <div className="text-left hidden sm:block">
                <div className={`font-medium text-sm lg:text-base ${
                  currentStep === step.id ? 'text-primary' : ''
                }`}>
                  {step.title}
                </div>
                <div className="text-xs text-gray-500 hidden lg:block">
                  {step.description}
                </div>
              </div>
            </button>
          ))}
        </div>
        
        {/* Automation Status Info */}
        <div className="lg:ml-auto mt-2 lg:mt-0 px-2 lg:px-0">
          <div className="text-center lg:text-right">
            <div className="text-xs lg:text-sm text-gray-600">
              Configure your review flow here
            </div>
            <div className="text-xs text-gray-500 hidden lg:block">
              Enable automation in Settings to auto-send
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <StepReviewQuestions 
            campaignData={campaignData}
            onDataChange={onDataChange}
            onSave={onSaveStep ? (stepData) => onSaveStep(1, stepData) : onSave}
            onMessage={onMessage}
          />
        );

      case 2:
        return (
          <StepEmailContent 
            campaignData={campaignData}
            onDataChange={onDataChange}
            onSave={onSaveStep ? (stepData) => onSaveStep(2, stepData) : onSave}
            onMessage={onMessage}
          />
        );

      case 3:
        return (
          <StepThankYouPages 
            campaignData={campaignData}
            onDataChange={onDataChange}
            onSave={onSaveStep ? (stepData) => onSaveStep(3, stepData) : onSave}
            onMessage={onMessage}
            onActiveTabChange={setStep3ActiveTab}
          />
        );

      default:
        return null;
    }
  };

  const renderPreview = () => {
    if (currentStep === 3) {
      // Thank You Pages Preview - dynamic based on active tab
      const getPreviewContent = () => {
        switch (step3ActiveTab) {
          case 'low':
            return {
              headline: campaignData.thank_you_pages?.low?.headline || "We're sorry you had a poor experience",
              message: campaignData.thank_you_pages?.low?.message || "Your feedback is important to us. Please let us know what went wrong so we can make it right.",
              buttonText: campaignData.thank_you_pages?.low?.buttonText || "Contact Us",
              showAI: false,
              showPlatforms: false,
              bgColor: 'bg-red-50',
              borderColor: 'border-red-200'
            };
          case 'neutral':
            return {
              headline: campaignData.thank_you_pages?.neutral?.headline || "Thank you for your feedback!",
              message: campaignData.thank_you_pages?.neutral?.message || "We appreciate you taking the time to share your experience with us. Your feedback helps us continue to improve our services.",
              showAI: false,
              showPlatforms: false,
              bgColor: 'bg-yellow-50',
              borderColor: 'border-yellow-200'
            };
          case 'high':
            return {
              headline: "Feedback Submitted!",
              message: campaignData.thank_you_pages?.high?.message || "We've generated a review draft based on your feedback. Feel free to copy and customize it, then share it on your preferred platform.",
              showAI: true,
              showPlatforms: true,
              bgColor: 'bg-gray-50',
              borderColor: 'border-gray-200'
            };
          default:
            return {};
        }
      };

      const previewContent = getPreviewContent();

      return (
        <div className="bg-white border border-gray-200 rounded-lg p-6 h-fit">
          <h4 className="font-medium text-gray-900 mb-4">Thank You Page Preview</h4>
          
          {/* Dynamic preview based on active tab */}
          <div className={`border rounded-lg p-6 ${previewContent.bgColor} ${previewContent.borderColor}`}>
            {step3ActiveTab === 'high' ? (
              /* High Rating Preview - matches actual page exactly */
              <div className="space-y-6">
                {/* Header with party emoji and proper styling */}
                <div className="text-center">
                  <div className="text-4xl mb-4">🎉</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {previewContent.headline}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {previewContent.message}
                  </p>
                </div>

                {/* AI Generated Review Card - exact styling from actual page */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-7 h-7 bg-blue-500 rounded-lg flex items-center justify-center">
                      <Icon name="Zap" size={14} className="text-white" />
                    </div>
                    <span className="font-semibold text-blue-900 text-sm">AI-Crafted Review</span>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border shadow-sm">
                    {/* 5 star rating */}
                    <div className="flex items-center gap-1 mb-2">
                      {[1,2,3,4,5].map(star => (
                        <span key={star} className="text-yellow-400 text-lg">★</span>
                      ))}
                    </div>
                    <p className="text-gray-800 text-sm leading-relaxed">
                      I had an urgent plumbing issue and reached out to {organizationData?.business_name || 'Dispatch Solutions'} for help. Their prompt response and expert handling of the situation truly impressed me. From the skilled technicians to their efficient service, {organizationData?.business_name || 'Dispatch Solutions'} exceeded all my expectations. I highly recommend them for any emergency plumbing needs.
                    </p>
                    <div className="mt-3 flex justify-end">
                      <span className="text-xs text-gray-500 flex items-center">
                        <Icon name="Edit" size={12} className="mr-1" />
                        Edit Review
                      </span>
                    </div>
                  </div>
                  
                  <div className="mt-4 flex justify-center">
                    <button className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm font-medium">
                      <Icon name="Copy" size={14} className="mr-2" />
                      Copy Review
                    </button>
                  </div>
                </div>

                {/* External Platform Button - exact styling */}
                <div className="text-center">
                  {campaignData.selected_review_platform && (campaignData.platform_urls?.[campaignData.selected_review_platform] || campaignData.review_platform_url) ? (
                    <button className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg font-semibold text-base shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center">
                      <Icon name="ExternalLink" size={18} className="mr-3" />
                      Post Review on {campaignData.selected_review_platform === 'yelp' ? 'Yelp' : campaignData.selected_review_platform === 'facebook' ? 'Facebook' : 'Google'}
                    </button>
                  ) : (
                    <button className="w-full py-4 bg-gray-300 text-gray-500 rounded-lg font-semibold text-base" disabled>
                      Select Platform & Add URL
                    </button>
                  )}
                </div>
              </div>
            ) : (
              /* Other rating previews */
              <div className="text-center space-y-4">
                <div className="text-2xl mb-4 font-semibold text-gray-900">
                  {previewContent.headline}
                </div>
                <div className="text-gray-600 mb-6">
                  {previewContent.message}
                </div>
                
                {/* Contact Button (for low ratings) */}
                {step3ActiveTab === 'low' && (
                  <button className="px-6 py-3 bg-primary text-white rounded-md text-sm font-medium">
                    {previewContent.buttonText}
                  </button>
                )}
              </div>
            )}
          </div>
          
          <div className="mt-4 text-xs text-gray-500">
            Preview updates based on selected rating level tab: {step3ActiveTab === 'low' ? '1-2 Stars' : step3ActiveTab === 'neutral' ? '3 Stars' : `${campaignData.positive_threshold || 4}+ Stars`}
          </div>
        </div>
      );
    } else if (currentStep === 2) {
      // Email Preview
      return (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 text-center">Preview</h3>
          
          {/* Email Preview */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            {/* Email Header */}
            <div className="bg-gray-100 px-4 py-3 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {replaceVariablesForPreview(campaignData.email_subject) || `How was your experience with ${organizationData?.name || 'Your Business'}?`}
                  </div>
                  <div className="text-xs text-gray-500">
                    {organizationData?.name || 'Your Business'} &lt;noreply@reviews.servicereview.ai&gt;
                  </div>
                </div>
              </div>
            </div>
            
            {/* Email Body */}
            <div className="p-6">
              {/* Email Title */}
              <h1 className="text-xl font-semibold text-gray-900 mb-4">
                {replaceVariablesForPreview(campaignData.email_title) || 'How was our service?'}
              </h1>
              
              <div className="mb-4">
                <div className="whitespace-pre-line text-gray-700 mb-4">
                  {replaceVariablesForPreview(
                    campaignData.email_body_template || 
                    "Hi {{contact.name}},\n\nThank you for choosing {{business.name}} for your recent service! We hope everything went smoothly.\n\nYour feedback is incredibly valuable to us and helps other customers make informed decisions. Would you mind taking a moment to share your experience?\n\nIt only takes 2 minutes and would mean the world to us."
                  )}
                </div>
              </div>
              
              {/* CTA Button */}
              <div className="text-center">
                <div className="inline-block bg-blue-600 text-white px-6 py-3 rounded-md font-medium">
                  {campaignData.cta_button_text || "Leave us a review"}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Default: Review Form Preview (Step 1)
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 text-center">Preview</h3>
        
        {/* Review Request Preview */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <div className="text-center mb-6">
            <h4 className="text-xl font-semibold text-gray-900 mb-2">
              {campaignData.title || "Review Request"}
            </h4>
            <p className="text-gray-600">
              {campaignData.intro_text || "Your feedback is important to us. Please take a moment to review us."}
            </p>
          </div>

          {/* Questions Preview */}
          <div className="space-y-4">
            {campaignData.questions
              .filter(question => question.enabled) // Only show enabled questions
              .map((question, index) => (
              <div key={index}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {question.label} {question.required && <span className="text-red-500">*</span>}
                </label>
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <span key={star} className="text-2xl text-gray-300">
                      ★
                    </span>
                  ))}
                </div>
              </div>
            ))}
            
            {/* Comment field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Comments (Optional)
              </label>
              <div className="w-full h-20 bg-gray-50 border border-gray-200 rounded-md"></div>
            </div>
          </div>
          
          <div className="mt-6 text-center">
            <div className="bg-primary text-white px-4 py-2 rounded-md text-sm font-medium">
              Submit Review
            </div>
          </div>
        </div>

        {/* Rating Flow Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h5 className="text-sm font-medium text-blue-900 mb-2">Rating Flow</h5>
          <div className="space-y-1 text-xs text-blue-800">
            <div>1-2 ★ → Private feedback form</div>
            {campaignData.positive_threshold === 3 ? (
              <div>3+ ★ → AI review + external platforms</div>
            ) : (
              <>
                <div>3{campaignData.positive_threshold === 5 ? '-4' : ''} ★ → Thank you page</div>
                <div>{campaignData.positive_threshold}+ ★ → AI review + external platforms</div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderNavigation = () => (
    <div className="flex flex-col sm:flex-row justify-between items-center space-y-2 sm:space-y-0">
      <Button
        onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
        disabled={currentStep === 1}
        variant="outline"
        className="w-full sm:w-auto"
      >
        <Icon name="ArrowLeft" size={16} className="mr-2" />
        Previous
      </Button>

      <div className="hidden sm:flex items-center space-x-4">
        {/* Step indicator for desktop */}
        <span className="text-sm text-gray-500">
          Step {currentStep} of {steps.length}
        </span>
      </div>

      <Button
        onClick={() => setCurrentStep(Math.min(3, currentStep + 1))}
        disabled={currentStep === 3}
        variant="primary"
        className="w-full sm:w-auto"
      >
        Next
        <Icon name="ArrowRight" size={16} className="ml-2" />
      </Button>
    </div>
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 min-h-[600px]">
      {/* Top Tabs */}
      {renderTopTabs()}
      
      {/* Main Content Area */}
      <div className="flex flex-col lg:flex-row">
        {/* Left Content */}
        <div className="flex-1 p-4 lg:p-6">
          {renderStepContent()}
        </div>
        
        {/* Right Preview */}
        <div className="w-full lg:w-96 border-t lg:border-t-0 lg:border-l border-gray-200 p-4 lg:p-6 bg-gray-50 order-first lg:order-last mb-4 lg:mb-0">
          <div className="lg:sticky lg:top-4">
            {renderPreview()}
          </div>
        </div>
      </div>
      
      {/* Bottom Navigation */}
      <div className="border-t border-gray-200 px-4 lg:px-6 py-3 lg:py-4">
        {renderNavigation()}
      </div>
    </div>
  );
};

export default CampaignBuilder; 